import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Loader2, Link as LinkIcon, FileText, Briefcase, CheckCircle, AlertTriangle, XCircle, ArrowRight, ArrowLeft, Upload, X, User, Target, Lightbulb, TrendingUp, Heart, Award, ListChecks, Info, Clock, Search } from "lucide-react";
import { ReportMarkdownRenderer } from "@/components/ReportMarkdownRenderer";
const PROGRESS_STAGES = ["Scraping job posting...", "Verifying company legitimacy...", "Analyzing hiring status...", "Identifying hiring manager...", "Researching company goals...", "Detecting pain points...", "Building separation strategy...", "Compiling final report..."];
export default function JobIntelReport() {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();

  // Form state - job description is required, URL and resume are optional
  const [jobText, setJobText] = useState("");
  const [jobUrl, setJobUrl] = useState("");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [progressStage, setProgressStage] = useState(0);
  const [report, setReport] = useState<any>(null);
  useEffect(() => {
    if (!user) {
      navigate("/auth");
    }
  }, [user, navigate]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPolling) {
      interval = setInterval(() => {
        setProgressStage(prev => (prev + 1) % PROGRESS_STAGES.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isPolling]);
  const pollForReport = async (startTime: Date) => {
    const pollInterval = setInterval(async () => {
      const {
        data,
        error
      } = await supabase.from('job_intel_reports').select('*').eq('user_id', user?.id).gte('created_at', startTime.toISOString()).in('status', ['completed', 'failed']).order('created_at', {
        ascending: false
      }).limit(1).maybeSingle();
      if (data) {
        clearInterval(pollInterval);
        setIsPolling(false);
        setReport(data);
        if (data.status === 'failed') {
          toast({
            title: "Report Failed",
            description: data.message || data.error || "Unable to generate report",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Report Complete",
            description: "Your JobIntel 360 Insider report is ready!"
          });
        }
      }
    }, 5000);

    // Stop polling after 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (isPolling) {
        setIsPolling(false);
        toast({
          title: "Timeout",
          description: "Report generation is taking longer than expected. Check your reports library later.",
          variant: "destructive"
        });
      }
    }, 600000);
  };
  const handleResumeUpload = useCallback(async (file: File) => {
    if (!user?.id) return;
    setIsUploadingResume(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/job-intel-${Date.now()}.${fileExt}`;
      const {
        error
      } = await supabase.storage.from('uploaded-documents').upload(fileName, file);
      if (error) throw error;
      const {
        data
      } = supabase.storage.from('uploaded-documents').getPublicUrl(fileName);
      setResumeUrl(data.publicUrl);
      setResumeFile(file);
      toast({
        title: "Resume uploaded",
        description: "Your resume has been uploaded successfully."
      });
    } catch (err: any) {
      console.error('Error uploading resume:', err);
      toast({
        title: "Upload failed",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setIsUploadingResume(false);
    }
  }, [user, toast]);
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleResumeUpload(file);
    }
  };
  const removeResume = () => {
    setResumeFile(null);
    setResumeUrl(null);
  };
  const handleSubmit = async () => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "You must be logged in",
        variant: "destructive"
      });
      return;
    }

    // Job description is required
    if (!jobText.trim()) {
      toast({
        title: "Error",
        description: "Please paste the job description (required)",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    setProgressStage(0);
    try {
      const payload: Record<string, string> = {
        user_id: user.id,
        job_posting_text: jobText.trim()
      };

      // Add optional fields if provided
      if (jobUrl.trim()) {
        payload.job_url = jobUrl.trim();
      }
      if (resumeUrl) {
        payload.resume_url = resumeUrl;
      }
      const {
        error
      } = await supabase.functions.invoke('trigger-job-intel', {
        body: payload
      });
      if (error) throw error;
      toast({
        title: "Report Requested",
        description: "Generating your JobIntel 360 Insider report. This typically takes 2-5 minutes."
      });
      setIsPolling(true);
      pollForReport(new Date());
    } catch (err: any) {
      console.error('Error triggering report:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to request report",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const getVerdictBadge = (verdict: string) => {
    switch (verdict) {
      case 'APPLY':
        return <Badge className="bg-success text-success-foreground"><CheckCircle className="w-4 h-4 mr-1" /> Apply</Badge>;
      case 'CAUTION':
        return <Badge className="bg-warning text-warning-foreground"><AlertTriangle className="w-4 h-4 mr-1" /> Caution</Badge>;
      case 'DONT_APPLY':
        return <Badge className="bg-destructive text-destructive-foreground"><XCircle className="w-4 h-4 mr-1" /> Don't Apply</Badge>;
      default:
        return <Badge variant="secondary">{verdict}</Badge>;
    }
  };

  // Report display (when completed)
  if (report && report.status === 'completed') {
    return <div className="container mx-auto py-8 px-4 max-w-4xl">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <Button variant="outline" onClick={() => navigate('/job-intel-reports')}>
            View All Reports
          </Button>
        </div>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">JobIntel 360 Insider Report</h1>
            <p className="text-muted-foreground">{report.company_name} - {report.job_title}</p>
          </div>
          {report.verdict && getVerdictBadge(report.verdict)}
        </div>

        {/* 8-Section Report Display */}
        <div className="space-y-6">
          {/* Section 1: Job Overview */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" />
                Job Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Company:</span> <strong>{report.company_name || 'N/A'}</strong></div>
                <div><span className="text-muted-foreground">Title:</span> <strong>{report.job_title || 'N/A'}</strong></div>
                {report.job_status?.posting_age_days && <div><span className="text-muted-foreground">Posting Age:</span> <strong>{report.job_status.posting_age_days} days</strong></div>}
                {report.job_status?.company_hiring_status && <div><span className="text-muted-foreground">Hiring Status:</span> <strong>{report.job_status.company_hiring_status}</strong></div>}
              </div>
            </CardContent>
          </Card>

          {/* Section 2: Worth Pursuing? (Verdict) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Worth Pursuing?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                {report.verdict && getVerdictBadge(report.verdict)}
                {report.job_status?.on_official_careers !== undefined && <Badge variant={report.job_status.on_official_careers ? "default" : "secondary"}>
                    {report.job_status.on_official_careers ? "✓ On Official Careers Page" : "⚠ Not on Official Careers"}
                  </Badge>}
              </div>
              {report.job_status?.timing_advantage && <p className="text-sm text-muted-foreground">{report.job_status.timing_advantage}</p>}
              {report.job_status?.red_flags && report.job_status.red_flags.length > 0 && <div className="mt-3">
                  <p className="text-sm font-medium text-destructive mb-2">Red Flags:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    {report.job_status.red_flags.map((flag: string, i: number) => <li key={i}>{flag}</li>)}
                  </ul>
                </div>}
            </CardContent>
          </Card>

          {/* Section 3: Pain Points */}
          {report.pain_points && report.pain_points.length > 0 && <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning" />
                  Pain Points (What They're Really Struggling With)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {report.pain_points.map((pain: any, i: number) => <div key={i} className="border-l-4 border-warning pl-4 py-2">
                      <p className="font-medium">{pain.problem}</p>
                      <p className="text-sm text-muted-foreground mt-1">{pain.evidence}</p>
                      {pain.your_angle && <p className="text-sm text-primary mt-2"><strong>Your Angle:</strong> {pain.your_angle}</p>}
                    </div>)}
                </div>
              </CardContent>
            </Card>}

          {/* Section 4: Strategic Goals */}
          {report.company_goals && report.company_goals.length > 0 && <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Strategic Goals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {report.company_goals.map((goal: any, i: number) => <div key={i} className="border-l-4 border-primary pl-4 py-2">
                      <p className="font-medium">{goal.goal}</p>
                      {goal.timeline && <p className="text-sm text-muted-foreground">Timeline: {goal.timeline}</p>}
                      {goal.your_angle && <p className="text-sm text-primary mt-2"><strong>Your Angle:</strong> {goal.your_angle}</p>}
                    </div>)}
                </div>
              </CardContent>
            </Card>}

          {/* Section 5: Hiring Manager Intel */}
          {report.hiring_manager?.name && <Card className="border-2 border-primary/20 bg-primary/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Hiring Manager Intel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-xl font-semibold">{report.hiring_manager.name}</p>
                    {report.hiring_manager.title && <p className="text-muted-foreground">{report.hiring_manager.title}</p>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {report.hiring_manager.linkedin_url && <a href={report.hiring_manager.linkedin_url} target="_blank" rel="noopener noreferrer">
                        <Badge className="bg-blue-600 hover:bg-blue-700 cursor-pointer">
                          View LinkedIn <ArrowRight className="w-3 h-3 ml-1" />
                        </Badge>
                      </a>}
                    {report.hiring_manager.email && <Badge variant="outline">{report.hiring_manager.email}</Badge>}
                  </div>
                  {report.hiring_manager.career_background && <p className="text-sm text-muted-foreground mt-2">{report.hiring_manager.career_background}</p>}
                  {report.hiring_manager.what_they_care_about && <div className="mt-3">
                      <p className="text-sm font-medium mb-1">What They Care About:</p>
                      <ul className="list-disc list-inside text-sm text-muted-foreground">
                        {report.hiring_manager.what_they_care_about.map((item: string, i: number) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>}
                  {report.hiring_manager.connection_hooks && <div className="mt-3">
                      <p className="text-sm font-medium mb-1">Connection Hooks:</p>
                      <ul className="list-disc list-inside text-sm text-muted-foreground">
                        {report.hiring_manager.connection_hooks.map((hook: string, i: number) => <li key={i}>{hook}</li>)}
                      </ul>
                    </div>}
                </div>
              </CardContent>
            </Card>}

          {/* Section 6: Emotional Hooks */}
          {report.hiring_manager?.pressure_theyre_under && <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Heart className="w-5 h-5 text-destructive" />
                  Emotional Hooks
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium">Pressure They're Under:</p>
                  <p className="text-muted-foreground">{report.hiring_manager.pressure_theyre_under}</p>
                </div>
                {report.hiring_manager.what_makes_them_look_good && <div>
                    <p className="text-sm font-medium">What Makes Them Look Good:</p>
                    <p className="text-muted-foreground">{report.hiring_manager.what_makes_them_look_good}</p>
                  </div>}
              </CardContent>
            </Card>}

          {/* Section 7: Separation Strategy */}
          {report.separation_strategy && <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Separation Strategy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {report.separation_strategy.positioning_statement && <div className="bg-primary/10 p-4 rounded-lg">
                    <p className="text-sm font-medium mb-1">Positioning Statement:</p>
                    <p className="italic">"{report.separation_strategy.positioning_statement}"</p>
                  </div>}
                {report.separation_strategy.outreach_opening_line && <div>
                    <p className="text-sm font-medium mb-1">Opening Line for Outreach:</p>
                    <p className="text-muted-foreground">"{report.separation_strategy.outreach_opening_line}"</p>
                  </div>}
              </CardContent>
            </Card>}

          {/* Section 8: Next Steps / Resume Tips */}
          {report.resume_tips && <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ListChecks className="w-5 h-5 text-success" />
                  Next Steps & Resume Tips
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{report.resume_tips}</p>
              </CardContent>
            </Card>}

          {/* Full Report (Expandable) */}
          {report.report && <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Full Report
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ReportMarkdownRenderer content={report.report} />
              </CardContent>
            </Card>}
        </div>

        <div className="mt-6 flex gap-4">
          <Button onClick={() => {
          setReport(null);
          setJobUrl('');
          setJobText('');
          setResumeFile(null);
          setResumeUrl(null);
        }}>
            Generate Another Report
          </Button>
          <Button variant="outline" onClick={() => navigate('/job-intel-reports')}>
            View All Reports
          </Button>
        </div>
      </div>;
  }

  // Main input form with new design
  return <div className="min-h-screen bg-orange-50/50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/jobintel-360-reports')} className="h-9 w-9 p-0 bg-white">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
              <Briefcase className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">JobIntel 360 - Official Insider Report</h1>
              <p className="text-muted-foreground mt-1">
                The #1 most comprehensive job intelligence report ever built!
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/job-intel-reports')} className="gap-2 bg-white">
            <FileText className="h-4 w-4" />
            My Reports
          </Button>
        </div>

        {/* Hero Card */}
        <Card className="border-2 border-orange-400 bg-orange-50 shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Get the Unfair Advantage Before You Apply
              </h2>
              <p className="text-muted-foreground max-w-4xl mx-auto leading-relaxed my-0 px-0">
                The JobIntel 360 Insider digs deeper than the job description ever will—researching the company, department, industry, and hiring manager using LinkedIn, Glassdoor, news, SEC filings, and employee reviews to uncover the real pain points the role exists to solve, red flags to watch for, and what the hiring manager actually cares about.
              </p>
              <p className="text-muted-foreground max-w-4xl mx-auto leading-relaxed mt-4 px-[42px]">
                This is <span className="font-bold text-foreground">pure job intelligence</span>. Armed with it, you can craft applications, resumes, and outreach<br />that speak directly to what they need—<span className="font-bold text-foreground">while your competition is still guessing</span>.
              </p>
            </div>

            {/* Two Info Links */}
            <div className="flex items-center justify-center gap-6 mt-4 mb-8">
              {/* Link 1: Why this matters (Amber themed) */}
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-amber-600 hover:text-amber-700 underline decoration-amber-400 underline-offset-4 font-semibold transition-colors">
                    <AlertTriangle className="w-4 h-4 mr-1.5" />
                    Why this matters
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-600">
                      <AlertTriangle className="w-5 h-5" />
                      Top 10 Job Search Statistics (2025–2026)
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[65vh] pr-4">
                    <div className="space-y-6">
                      {/* The Statistics */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-sm text-amber-900/80">
                          Here are the top 10 strongest, data-backed statistics that show why job seekers need deep job intelligence like the JobIntel 360 Insider Report — especially in today's competitive and increasingly automated hiring landscape.
                        </p>
                      </div>

                      <div className="space-y-3">
                        {[{
                        num: 1,
                        title: "Less than 3% of applications lead to interviews.",
                        desc: "New hiring data shows only 2–3% of candidates who apply online get invited to an interview — meaning 97–98% of applicants are filtered out before human review."
                      }, {
                        num: 2,
                        title: "Up to 75% of resumes are rejected by ATS before a human ever sees them.",
                        desc: "The majority of applications are screened out by Applicant Tracking Systems due to keywords, formatting, or lack of match."
                      }, {
                        num: 3,
                        title: "90% of employers use ATS software.",
                        desc: "Most large companies rely on automated screening, creating a barrier for generic or unoptimized resumes."
                      }, {
                        num: 4,
                        title: "A single job posting attracts ~250 applications on average.",
                        desc: "That means massive competition for every opening — increasing the value of strategic insights and differentiation."
                      }, {
                        num: 5,
                        title: "Tailored resumes nearly double interview conversion rates.",
                        desc: "Candidates who customize their resumes for specific jobs see significantly higher success (e.g., ~5.8% vs ~3.7%) compared to generic ones."
                      }, {
                        num: 6,
                        title: "Median time to first offer now averages ~83 days.",
                        desc: "Job seekers face a longer, slower hiring cycle — making smart targeting and preparation essential to reduce wasted time."
                      }, {
                        num: 7,
                        title: "Smaller, niche job platforms out-perform big boards.",
                        desc: "Response rates vary widely: Google Jobs ~11.3%, GovernmentJobs ~8.7%, compared with LinkedIn at just ~3.1%. This highlights the need for targeted platform strategy, not generic applying."
                      }, {
                        num: 8,
                        title: "Only about 10% of job seekers believe their resumes are seen by humans.",
                        desc: "This shows growing candidate skepticism — and a real need for strategies that beat ATS filters and reach hiring managers."
                      }, {
                        num: 9,
                        title: "Recruiters spend 6–30 seconds reviewing a resume initially.",
                        desc: "With such limited attention, irrelevant or generic applications often get skipped — research helps focus on what matters most."
                      }, {
                        num: 10,
                        title: "It can take 21–80+ applications to get one job offer.",
                        desc: "With low conversion rates and high application volumes, quality over quantity — backed by research intelligence — becomes critical."
                      }].map(stat => <div key={stat.num} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-amber-200">
                            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-xs">{stat.num}</div>
                            <div>
                              <p className="font-semibold text-foreground text-sm">{stat.title}</p>
                              <p className="text-xs text-muted-foreground mt-1">{stat.desc}</p>
                            </div>
                          </div>)}
                      </div>

                      <Separator />

                      {/* What These Numbers Mean */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">📌 What These Numbers Mean for Job Seekers</h3>
                        <div className="space-y-3">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground"><strong className="text-foreground">Generic applications almost never get seen.</strong> With 97–98% of applicants filtered out before human eyes, applying without strategy is typically ineffective.</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground"><strong className="text-foreground">ATS optimization is not optional.</strong> Since up to 75% of resumes are rejected by automated systems, understanding how to craft and position your resume strategically is essential — and that's exactly what JobIntel 360 intel supports.</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground"><strong className="text-foreground">Competition is intense.</strong> 250+ applicants per role and median offer timelines near 3 months make pre-application research, targeting the right roles, and understanding the hiring context vital.</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground"><strong className="text-foreground">Tailored applications give measurable boosts.</strong> Personalization significantly improves interview rates — which makes knowing the company, hiring manager, and role goals far more valuable than generic submitting.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>

              <span className="text-muted-foreground">|</span>

              {/* Link 2: What you'll discover (Orange themed) */}
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-orange-600 hover:text-orange-700 underline decoration-orange-400 underline-offset-4 font-semibold transition-colors">
                    <Info className="w-4 h-4 mr-1.5" />
                    What you'll discover →
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-orange-600">
                      <Lightbulb className="w-5 h-5" />
                      What You'll Learn in Your Report
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[75vh] pr-4">
                    <div className="space-y-6">
                      {/* Overview Section */}
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <h3 className="font-semibold text-orange-800 mb-2">JobIntel 360 Job Insider Report</h3>
                        <p className="text-sm text-orange-900/80">
                          The JobIntel 360 Insider is a comprehensive job intelligence report that gives job seekers an <span className="font-bold">unfair advantage</span> by deeply researching everything about a job opportunity before they apply. Unlike generic job descriptions, it investigates the company, the specific department, the industry landscape, and most importantly—<span className="font-bold">the actual hiring manager</span>—using multi-source evidence (LinkedIn, Glassdoor, news, SEC filings, employee reviews, etc.) to uncover the real pain points the role exists to solve, red flags to watch for, and exactly what the hiring manager cares about.
                        </p>
                        <p className="text-sm text-orange-900/80 mt-3">
                          It's <span className="font-bold">pure job intelligence</span>, not candidate-specific positioning—any applicant can use it to understand the opportunity at a level their competition won't. Armed with this insider intelligence, you can craft applications, resumes, and outreach that speak directly to what the hiring manager actually needs—a level of precision and relevance that candidates relying solely on the job description will never match.
                        </p>
                      </div>

                      <Separator />

                      {/* What You'll Learn List */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-4">What You'll Learn:</h3>
                        <div className="space-y-4">
                          {[{
                          icon: Target,
                          color: "text-green-600",
                          border: "border-green-500",
                          title: "Apply or Skip Verdict",
                          desc: "A clear recommendation on whether to pursue this opportunity, with evidence-based reasoning"
                        }, {
                          icon: AlertTriangle,
                          color: "text-amber-600",
                          border: "border-amber-500",
                          title: "Job Status & Legitimacy",
                          desc: "Is this posting still active, how old is it, is it a ghost job, and is the company actually hiring?"
                        }, {
                          icon: Briefcase,
                          color: "text-blue-600",
                          border: "border-blue-500",
                          title: "Why This Role Exists Now",
                          desc: "Is it a replacement, expansion, or new need? What triggered the hire and what's the cost of leaving it unfilled?"
                        }, {
                          icon: ListChecks,
                          color: "text-cyan-600",
                          border: "border-cyan-500",
                          title: "What to Expect If You Get the Job",
                          desc: "First 90 days priorities, immediate challenges you'll face, resources available, team dynamics, and realistic expectations for the role"
                        }, {
                          icon: User,
                          color: "text-purple-600",
                          border: "border-purple-500",
                          title: "Hiring Manager Deep Dive",
                          desc: "Name, title, LinkedIn, verified contact info, full career trajectory, leadership style, communication preferences, what frustrates them, and what makes them look good to their boss"
                        }, {
                          icon: TrendingUp,
                          color: "text-indigo-600",
                          border: "border-indigo-500",
                          title: "Industry Analysis",
                          desc: "Market size, growth trends, tailwinds/headwinds, where the industry is headed 2025-2027, and where this company sits vs. competitors"
                        }, {
                          icon: Briefcase,
                          color: "text-slate-600",
                          border: "border-slate-500",
                          title: "Company Intel",
                          desc: "Glassdoor/Indeed ratings, recent news (positive and negative), financial health, culture themes, and employee sentiment"
                        }, {
                          icon: User,
                          color: "text-teal-600",
                          border: "border-teal-500",
                          title: "Department/Team Analysis",
                          desc: "Team size, recent changes, current goals, who else works there, turnover patterns, and what's working vs. struggling"
                        }, {
                          icon: Award,
                          color: "text-green-600",
                          border: "border-green-500",
                          title: "Salary & Compensation Intel",
                          desc: "Finds the salary range even when not listed (via Glassdoor, Levels.fyi, Payscale, employee reviews, similar postings), plus bonus structures, equity, and how this compares to market rate"
                        }, {
                          icon: Heart,
                          color: "text-emerald-600",
                          border: "border-emerald-500",
                          title: "Benefits Deep Dive",
                          desc: "Insurance providers (medical, dental, vision), 401k match details, PTO policies, parental leave, remote work stipends, and other perks uncovered from employee reviews and benefits databases"
                        }, {
                          icon: Heart,
                          color: "text-pink-600",
                          border: "border-pink-500",
                          title: "Culture & Work Environment",
                          desc: "Work-life balance, remote/hybrid policies, management style themes, what employees love vs. complain about, and cultural keywords that define the day-to-day experience"
                        }, {
                          icon: AlertTriangle,
                          color: "text-red-600",
                          border: "border-red-500",
                          title: "Evidence-Backed Pain Points",
                          desc: "The real problems this hire is meant to solve, with multi-source evidence, who feels the pain, what's at stake, and why it hasn't been fixed yet"
                        }, {
                          icon: AlertTriangle,
                          color: "text-amber-600",
                          border: "border-amber-500",
                          title: "Red Flags & Interview Questions",
                          desc: "Concerns to probe diplomatically, plus specific questions that demonstrate research depth"
                        }, {
                          icon: ArrowRight,
                          color: "text-violet-600",
                          border: "border-violet-500",
                          title: "Recommended Outreach Approach",
                          desc: "Best channel, timing, tone, and specific connection hooks to use when reaching out"
                        }].map((item, i) => <div key={i} className={`border-l-4 ${item.border} pl-4`}>
                              <h4 className={`font-semibold text-foreground flex items-center gap-2 ${item.color}`}>
                                <item.icon className="w-5 h-5" />
                                {item.title}
                              </h4>
                              <p className="text-sm text-muted-foreground mt-1">{item.desc}</p>
                            </div>)}
                        </div>
                      </div>

                      <Separator />

                      {/* The Result */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h3 className="font-semibold text-green-800 mb-2">The Results You Can Expect</h3>
                        <div className="space-y-2 text-sm text-green-900/80">
                          <p className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" /> <strong>85%</strong> interview acquisition rate</p>
                          <p className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" /> <strong>2–5 interviews per week</strong> within 14–28 days</p>
                          <p className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" /> <strong>1 in 3</strong> interviews leads to an offer</p>
                          <p className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" /> Job search time cut by <strong>70%</strong></p>
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </div>

            {/* 4 Feature Boxes */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 text-center border-2 border-orange-600 shadow-sm">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Target className="w-5 h-5 text-orange-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">Apply or Skip</div>
                <div className="text-xs text-muted-foreground">Clear verdict</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border-2 border-orange-600 shadow-sm">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <User className="w-5 h-5 text-orange-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">Hiring Manager</div>
                <div className="text-xs text-muted-foreground">Name, title, LinkedIn</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border-2 border-orange-600 shadow-sm">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">Pain Points</div>
                <div className="text-xs text-muted-foreground">Evidence-backed</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border-2 border-orange-600 shadow-sm">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Award className="w-5 h-5 text-orange-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">Salary Intel</div>
                <div className="text-xs text-muted-foreground">Even when not listed</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Polling State */}
        {isPolling ? <Card className="border-2 border-orange-400 bg-white shadow-lg">
            <CardContent className="py-12 text-center">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-orange-600" />
              <h3 className="text-xl font-semibold mb-2">Generating Your Report</h3>
              <p className="text-muted-foreground mb-4">{PROGRESS_STAGES[progressStage]}</p>
              <div className="flex justify-center gap-1">
                {PROGRESS_STAGES.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full ${i <= progressStage ? 'bg-orange-500' : 'bg-muted'}`} />)}
              </div>
              <p className="text-sm text-muted-foreground mt-4">This typically takes 2-5 minutes</p>
            </CardContent>
          </Card> : (/* Form Section */
      <Card className="border-2 border-orange-400 bg-white shadow-lg">
            <CardHeader className="bg-orange-50 border-b border-orange-200">
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-orange-600" />
                Enter Job Information
              </CardTitle>
              <CardDescription>
                Paste the job description to generate your comprehensive intel report
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Job Description (Required) */}
              <div className="space-y-2">
                <Label htmlFor="jobText" className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  Job Description <span className="text-destructive">*</span>
                </Label>
                <Textarea id="jobText" placeholder="Paste the full job description here..." value={jobText} onChange={e => setJobText(e.target.value)} rows={8} className="resize-none border-2 border-gray-400" />
                <p className="text-xs text-muted-foreground">Required - paste the complete job posting text</p>
              </div>

              {/* Job URL (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="jobUrl" className="flex items-center gap-1">
                  <LinkIcon className="w-4 h-4" />
                  Job Posting URL <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                <Input id="jobUrl" type="url" placeholder="https://linkedin.com/jobs/view/..." value={jobUrl} onChange={e => setJobUrl(e.target.value)} className="border-2 border-gray-400" />
                <p className="text-xs text-muted-foreground">Helps verify job legitimacy and gather additional context</p>
              </div>

              {/* Resume Upload (Optional) */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Upload className="w-4 h-4" />
                  Your Resume <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                {resumeFile ? <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                    <FileText className="w-4 h-4 text-orange-600" />
                    <span className="text-sm flex-1 truncate">{resumeFile.name}</span>
                    <Button size="sm" variant="ghost" onClick={removeResume}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div> : <>
                    <input id="resume-upload" type="file" accept=".pdf,.doc,.docx" onChange={handleFileSelect} className="hidden" />
                    <label htmlFor="resume-upload" className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors border-muted-foreground/25 hover:border-orange-400 bg-muted/50 hover:bg-orange-50 ${isUploadingResume ? 'opacity-50 pointer-events-none' : ''}`}>
                      {isUploadingResume ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-muted-foreground" />}
                      <span className="text-sm text-muted-foreground">
                        {isUploadingResume ? 'Uploading...' : 'Upload resume (PDF, DOC, DOCX)'}
                      </span>
                    </label>
                  </>}
                <p className="text-xs text-muted-foreground">Get personalized resume tips tailored to this job</p>
              </div>

              <Button className="w-full bg-orange-600 hover:bg-orange-700" size="lg" onClick={handleSubmit} disabled={isSubmitting || !jobText.trim()}>
                {isSubmitting ? <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </> : <>
                    <Briefcase className="w-4 h-4 mr-2" />
                    Generate JobIntel 360 Insider Report
                  </>}
              </Button>
            </CardContent>
          </Card>)}
      </div>
    </div>;
}